import { useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  Box,
  Alert,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import { useSnackbar } from "notistack";
import axios from "axios";
import { ProjectConfig } from "Global";
import AutocompleteField from "elements/AutocompleteField";
import ImportPreviewer from "components/General/Import/ImportPreviewer";

export default function CreateRouteDialog(props: any) {
  const { open, setOpen, setLoading, loadList } = props;
  const { enqueueSnackbar } = useSnackbar();

  const [file, setFile] = useState<any>(null);
  const [vehicleIds, setVehicleIds] = useState<number[]>([]);
  const [previewData, setPreviewData] = useState<any>({ headers: [], rows: [] });
  const [openPreviewer, setOpenPreviewer] = useState(false);
  const [meta, setMeta] = useState<any>(null);
  const [assignmentMode, setAssignmentMode] = useState<string>('by_quantity');

  const resetState = () => {
    setFile(null);
    setVehicleIds([]);
    setPreviewData({ headers: [], rows: [] });
    setMeta(null);
    setAssignmentMode('by_quantity');
  };

  const handleClose = () => {
    setOpen(false);
    resetState();
  };

  const handleSubmit = async () => {
    if (!file) {
      enqueueSnackbar("Selecciona un archivo .xlsx", { variant: "warning" });
      return;
    }

    setLoading(true);

    const formData = new FormData();
    formData.append("files", file);
    formData.append(
      "formValues",
      JSON.stringify({
        upload_type: "route_auto",
        vehicle_ids: vehicleIds,
        assignment_mode: assignmentMode,
      })
    );

    let delegated_company_id: string | null = null;
    if (localStorage.getItem("delegated_company_id")) {
      delegated_company_id = localStorage.getItem("delegated_company_id");
    }

    try {
      const response = await axios.post(
        ProjectConfig.api_url + "/sync/checkRouteAutoImport",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            ltkn: localStorage.getItem("ltkn"),
            delegated_company_id,
          },
        }
      );

      const data = response.data;
      if (data.status === "success") {
        enqueueSnackbar(
          `${data.importedQuantity} guías listas para importar (${data.meta?.total_vehicles ?? "?"} vehículos)`,
          { variant: "success" }
        );
        setPreviewData(data.data);
        setMeta(data.meta);
        setOpenPreviewer(true);
      } else {
        enqueueSnackbar(data.message || "Error al validar archivo", { variant: "error" });
      }
    } catch (err: any) {
      console.error(err);
      enqueueSnackbar(
        err?.response?.data?.error || err?.message || "Error de red",
        { variant: "error" }
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span>Crear Ruta</span>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              Sube un archivo Excel (.xlsx) con las columnas: <b>CLIENTE, VALOR, FACTURA, PRIORIDAD, CARGUE</b> y opcionalmente <b>PESO</b> (kg).
              Las coordenadas se toman automáticamente de cada cliente y el número de guía se genera por vehículo.
              El número de <b>CARGUE</b> debe ser único por empresa.
            </Typography>
          </Alert>

          <Box sx={{ mb: 2 }}>
            <AutocompleteField
              label="Vehículos (opcional — vacío = todos los activos)"
              endpoint="/vehicles/list"
              endpoint_params={{ full_list: true }}
              value={vehicleIds}
              onChange={(ids: any) => setVehicleIds(ids || [])}
              multiple={true}
            />
          </Box>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Modo de asignación</InputLabel>
            <Select
              value={assignmentMode}
              label="Modo de asignación"
              onChange={(e) => setAssignmentMode(e.target.value)}
            >
              <MenuItem value="by_quantity">Por cupo (cantidad de pedidos)</MenuItem>
              <MenuItem value="by_weight">Por peso (kg)</MenuItem>
            </Select>
          </FormControl>

          <Box>
            <div
              className={`file_dropzone ${file && "file_dropzone_active"}`}
              onDrop={(e) => {
                e.preventDefault();
                const files = e.dataTransfer.files;
                if (files && files[0]) setFile(files[0]);
                e.currentTarget.classList.remove("dropzone_active");
              }}
              onDragOver={(e) => {
                e.preventDefault();
                e.currentTarget.classList.add("dropzone_active");
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                e.currentTarget.classList.remove("dropzone_active");
              }}
            >
              {file && <span>{file.name}</span>}

              <label id="dropFileDiv" className={file ? "showOnlyOnHover" : ""}>
                <AttachFileOutlinedIcon />
                <span>Arrastra o da click para subir un .xlsx</span>
                <input
                  type="file"
                  hidden
                  accept=".xlsx"
                  onChange={(e) => {
                    const files = e.target.files;
                    if (files && files[0]) setFile(files[0]);
                  }}
                />
              </label>
            </div>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} variant="outlined">
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            startIcon={<CloudUploadOutlinedIcon />}
          >
            Validar y previsualizar
          </Button>
        </DialogActions>
      </Dialog>

      <ImportPreviewer
        open={openPreviewer}
        setOpen={setOpenPreviewer}
        data={previewData}
        upload_type="route_auto"
        submitEndpoint="/sync/routeAutoImport"
        extraPayload={{ vehicle_ids: vehicleIds, assignment_mode: assignmentMode }}
        onImported={() => {
          if (typeof loadList === "function") loadList();
          handleClose();
        }}
        summaryContent={meta && (
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <Chip label={`Pedidos: ${meta.total_guides}`} color="primary" variant="outlined" size="small" />
            <Chip label={`Valor: $${Number(meta.total_value || 0).toLocaleString('es-CO')}`} color="primary" variant="outlined" size="small" />
            <Chip label={`Promedio: $${meta.total_guides ? Math.round((meta.total_value || 0) / meta.total_guides).toLocaleString('es-CO') : 0}`} color="primary" variant="outlined" size="small" />
            <Chip label={`Peso: ${meta.total_weight ? `${meta.total_weight} kg` : 'N/A'}`} color="default" variant="outlined" size="small" />
            <Chip label={`Distancia: ${meta.total_distance_km ?? 0} km`} color="info" variant="outlined" size="small" />
            <Chip label={`Dist. promedio: ${meta.avg_distance_km ?? 0} km`} color="info" variant="outlined" size="small" />
          </Box>
        )}
      />
    </>
  );
}
